import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plus, Trash2, Edit3, Search, Database, X, ChefHat, AlertTriangle, Sparkles, Loader2, Calculator } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { key: "all", label: "Tutte" },
  { key: "antipasto", label: "Antipasti" },
  { key: "primo", label: "Primi" },
  { key: "secondo", label: "Secondi" },
  { key: "contorno", label: "Contorni" },
  { key: "dolce", label: "Dolci" },
  { key: "pizza", label: "Pizze" },
];

const fmtEur = (n) => `€ ${Number(n).toFixed(2)}`;

export default function Dishes() {
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("all");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // dish object or "new"
  const [aiModal, setAiModal] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const r = await api.get("/dishes", { params: { category: cat, search: search || undefined } });
    setItems(r.data);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cat]);

  const onSearch = (e) => {
    e.preventDefault();
    load();
  };

  const handleSave = async (dish) => {
    try {
      if (dish.id) await api.put(`/dishes/${dish.id}`, dish);
      else await api.post(`/dishes`, dish);
      toast.success("Piatto salvato");
      setEditing(null);
      load();
    } catch { toast.error("Errore nel salvataggio"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questo piatto?")) return;
    await api.delete(`/dishes/${id}`);
    toast.success("Piatto eliminato");
    load();
  };

  const handleReseed = async () => {
    if (!window.confirm("Ricaricare il database con i 200+ piatti italiani standard? Questa azione cancellerà tutti i piatti esistenti.")) return;
    await api.post("/dishes/seed");
    toast.success("Database piatti ricaricato");
    load();
  };

  const handleClearAll = async () => {
    if (!window.confirm("⚠️ ATTENZIONE: cancellare TUTTI i piatti dal database?")) return;
    await api.delete("/dishes");
    toast.success("Database piatti svuotato");
    load();
  };

  const handleRecalc = async () => {
    if (!window.confirm("Ricalcolare food cost e prezzo di vendita di tutti i piatti basandosi sui prezzi ingredienti correnti?")) return;
    try {
      const r = await api.post("/dishes/recalc");
      const pct = Math.round((r.data.food_cost_ratio || 0.30) * 100);
      toast.success(`Ricalcolati ${r.data.recalculated} piatti (food cost = ${pct}% del prezzo)`);
      load();
    } catch {
      toast.error("Errore nel ricalcolo");
    }
  };

  return (
    <div className="space-y-8" data-testid="dishes-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-[#2563EB] font-semibold font-ui">Cucina</p>
          <h1 className="font-display text-4xl md:text-5xl text-[#0F2741] tracking-tight mt-2">Database Piatti</h1>
          <p className="text-slate-500 mt-2">{items.length} piatti caricati · costi medi mercato italiano</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={handleClearAll} data-testid="btn-clear-dishes" className="btn-ghost border border-rose-200 text-rose-600 hover:bg-rose-50">
            <AlertTriangle className="w-4 h-4" /> Cancella DB
          </button>
          <button onClick={handleRecalc} data-testid="btn-recalc-dishes" className="btn-ghost border border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100">
            <Calculator className="w-4 h-4" /> Ricalcola prezzi
          </button>
          <button onClick={handleReseed} data-testid="btn-reseed-dishes" className="btn-ghost border border-slate-200">
            <Database className="w-4 h-4" /> Ricarica DB
          </button>
          <button onClick={() => setAiModal(true)} data-testid="btn-ai-dish" className="btn-ghost border border-[#2563EB]/40 text-[#1D4ED8] bg-[#EFF6FF] hover:bg-[#DBEAFE]">
            <Sparkles className="w-4 h-4" /> Crea con AI
          </button>
          <button onClick={() => setEditing({})} data-testid="btn-new-dish" className="btn-primary">
            <Plus className="w-4 h-4" /> Nuovo piatto
          </button>
        </div>
      </div>

      <form onSubmit={onSearch} className="flex gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="input-dish-search"
            placeholder="Cerca un piatto..."
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-slate-200 focus:ring-2 focus:ring-[#2563EB] focus:border-transparent outline-none text-sm"
          />
        </div>
        <button type="submit" className="btn-secondary">Cerca</button>
      </form>

      <div className="flex flex-wrap gap-2">
        {CATEGORIES.map((c) => (
          <button
            key={c.key}
            onClick={() => setCat(c.key)}
            data-testid={`cat-${c.key}`}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${cat === c.key ? "bg-[#0F2741] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-[#2563EB]"}`}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500 tracking-wider">
            <tr>
              <th className="text-left px-6 py-3">Nome</th>
              <th className="text-left px-6 py-3">Categoria</th>
              <th className="text-left px-6 py-3">Ingredienti</th>
              <th className="text-right px-6 py-3">Peso</th>
              <th className="text-right px-6 py-3">Food cost</th>
              <th className="text-right px-6 py-3">Prezzo vendita</th>
              <th className="text-right px-6 py-3">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-6 py-12 text-center text-slate-400">Caricamento...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="px-6 py-12 text-center">
                <ChefHat className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                <p className="text-slate-500">Nessun piatto trovato</p>
              </td></tr>
            ) : items.map((d) => {
              const fcPct = d.sale_price_eur > 0 ? Math.round((d.cost_eur / d.sale_price_eur) * 100) : 0;
              return (
              <tr key={d.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                <td className="px-6 py-3 font-medium text-[#0F2741]">{d.name}</td>
                <td className="px-6 py-3"><span className="text-xs px-2 py-1 rounded-full bg-[#EFF6FF] text-[#1D4ED8] capitalize">{d.category}</span></td>
                <td className="px-6 py-3 text-slate-500 text-xs max-w-md truncate">{d.ingredients?.join(", ")}</td>
                <td className="px-6 py-3 text-right text-slate-600">{d.weight_g}g</td>
                <td className="px-6 py-3 text-right font-semibold text-amber-700" data-testid={`dish-cost-${d.id}`}>
                  <div>{fmtEur(d.cost_eur)}</div>
                  {fcPct > 0 && <div className="text-[10px] text-slate-400 font-normal mt-0.5">FC {fcPct}%</div>}
                </td>
                <td className="px-6 py-3 text-right font-semibold text-emerald-700" data-testid={`dish-sale-${d.id}`}>
                  {fmtEur(d.sale_price_eur)}
                </td>
                <td className="px-6 py-3 text-right">
                  <button onClick={() => setEditing(d)} className="text-slate-400 hover:text-[#2563EB] p-1.5"><Edit3 className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(d.id)} className="text-slate-400 hover:text-rose-500 p-1.5"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing !== null && <DishModal initial={editing} onClose={() => setEditing(null)} onSave={handleSave} />}
      {aiModal && <AIDishModal onClose={() => setAiModal(false)} onGenerated={(d) => { setAiModal(false); setEditing(d); }} />}
    </div>
  );
}

function AIDishModal({ onClose, onGenerated }) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState("primo");
  const [hints, setHints] = useState("");
  const [loading, setLoading] = useState(false);

  const generate = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setLoading(true);
    try {
      const r = await api.post("/dishes/generate-ai", { name: name.trim(), category, hints });
      toast.success("Piatto generato dall'AI — controlla e salva");
      onGenerated({
        name: r.data.name,
        category: r.data.category,
        ingredients: r.data.ingredients || [],
        weight_g: r.data.weight_g || 0,
        cost_eur: r.data.cost_eur || 0,
        sale_price_eur: r.data.sale_price_eur || 0,
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore generazione AI");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={onClose}>
      <form onSubmit={generate} onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl p-6 w-full max-w-md space-y-4" data-testid="ai-dish-modal">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#2563EB]" />
            <h3 className="font-display text-2xl text-[#0F2741]">Crea piatto con AI</h3>
          </div>
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <p className="text-sm text-slate-500">Inserisci il nome del piatto e l'AI suggerirà ingredienti, peso e costo stimato.</p>
        <div>
          <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Nome del piatto</label>
          <input
            data-testid="input-ai-dish-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
            placeholder="Es. Risotto allo zafferano"
            className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-[#2563EB] outline-none"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Categoria</label>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200">
            {CATEGORIES.filter((c) => c.key !== "all").map((c) => (<option key={c.key} value={c.key}>{c.label}</option>))}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Indicazioni (opzionale)</label>
          <textarea
            rows={2}
            value={hints}
            onChange={(e) => setHints(e.target.value)}
            placeholder="Es. vegetariano, senza glutine, ricco di verdure..."
            className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn-ghost">Annulla</button>
          <button type="submit" disabled={loading || !name.trim()} data-testid="btn-generate-ai-dish" className="btn-primary">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Generazione...</> : <><Sparkles className="w-4 h-4" /> Genera</>}
          </button>
        </div>
      </form>
    </div>
  );
}

function DishModal({ initial, onClose, onSave }) {
  // Convert legacy `ingredients: [name,...]` into `ingredient_quantities: [{name, weight_g}]`
  const initialQuantities = (() => {
    const iq = initial.ingredient_quantities || [];
    if (iq.length > 0) return iq.map((q) => ({ name: q.name, weight_g: q.weight_g || 0 }));
    const names = initial.ingredients || [];
    const totalW = initial.weight_g || 0;
    if (names.length === 0 || totalW === 0) return names.map((n) => ({ name: n, weight_g: 0 }));
    const share = Math.round(totalW / names.length);
    return names.map((n) => ({ name: n, weight_g: share }));
  })();

  const [form, setForm] = useState({
    id: initial.id,
    name: initial.name || "",
    category: initial.category || "primo",
    ingredient_quantities: initialQuantities,
    cost_eur: initial.cost_eur || 0,
    sale_price_eur: initial.sale_price_eur || 0,
  });
  const [ingredientDB, setIngredientDB] = useState([]);
  const [ingQuery, setIngQuery] = useState("");
  const [showSugg, setShowSugg] = useState(false);

  // Scroll page to top and lock body scroll so the modal is always centered/visible
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    window.scrollTo({ top: 0, behavior: "smooth" });
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);

  useEffect(() => {
    api.get("/ingredients").then((r) => setIngredientDB(r.data));
  }, []);

  // Compute live cost preview from quantities
  const totalWeight = form.ingredient_quantities.reduce((s, q) => s + (parseFloat(q.weight_g) || 0), 0);
  const livePreviewCost = form.ingredient_quantities.reduce((s, q) => {
    const ing = ingredientDB.find((i) => i.name.toLowerCase() === (q.name || "").toLowerCase());
    const price = ing ? parseFloat(ing.price_eur) || 0 : 0;
    const w = parseFloat(q.weight_g) || 0;
    return s + (w / 1000) * price;
  }, 0);
  const livePreviewSale = livePreviewCost > 0 ? Math.round(livePreviewCost / 0.30) : 0;

  const usedNames = form.ingredient_quantities.map((q) => q.name);
  const suggestions = ingQuery.length < 1 ? [] : ingredientDB
    .filter((i) => i.name.toLowerCase().includes(ingQuery.toLowerCase()) && !usedNames.includes(i.name))
    .slice(0, 8);

  const addIngredient = (name) => {
    if (!name || usedNames.includes(name)) return;
    const ing = ingredientDB.find((i) => i.name.toLowerCase() === name.toLowerCase());
    const defaultW = ing && ing.category ? defaultWeightForCategory(ing.category) : 50;
    setForm({
      ...form,
      ingredient_quantities: [...form.ingredient_quantities, { name, weight_g: defaultW }],
    });
    setIngQuery("");
    setShowSugg(false);
  };

  const updateQuantity = (idx, weight) => {
    const next = [...form.ingredient_quantities];
    next[idx] = { ...next[idx], weight_g: weight };
    setForm({ ...form, ingredient_quantities: next });
  };

  const removeIngredient = (idx) => {
    setForm({ ...form, ingredient_quantities: form.ingredient_quantities.filter((_, i) => i !== idx) });
  };

  const onIngKey = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addIngredient(ingQuery.trim().toLowerCase());
    }
  };

  const submit = (e) => {
    e.preventDefault();
    onSave({
      id: form.id,
      name: form.name,
      category: form.category,
      ingredients: form.ingredient_quantities.map((q) => q.name),
      ingredient_quantities: form.ingredient_quantities.map((q) => ({
        name: q.name,
        weight_g: parseFloat(q.weight_g) || 0,
      })),
      weight_g: totalWeight,
      cost_eur: parseFloat(form.cost_eur) || 0,
      sale_price_eur: parseFloat(form.sale_price_eur) || 0,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 overflow-y-auto p-4 sm:p-8" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl w-full max-w-2xl mx-auto my-auto shadow-2xl animate-in fade-in slide-in-from-top-4 duration-200 flex flex-col max-h-[92vh]" data-testid="dish-modal">
        <div className="flex justify-between items-center px-6 py-4 border-b border-slate-100 flex-shrink-0">
          <h3 className="font-display text-2xl text-[#0F2741]">{form.id ? "Modifica piatto" : "Nuovo piatto"}</h3>
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Nome</label>
            <input data-testid="input-dish-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-[#2563EB] outline-none" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Categoria</label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200">
              {CATEGORIES.filter((c) => c.key !== "all").map((c) => (<option key={c.key} value={c.key}>{c.label}</option>))}
            </select>
          </div>
        </div>

        <div>
          <div className="flex justify-between items-baseline">
            <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Ingredienti con quantità ({form.ingredient_quantities.length})</label>
            <span className="text-xs text-slate-400">Totale porzione: <strong className="text-[#0F2741]">{totalWeight.toFixed(0)}g</strong></span>
          </div>
          <div className="mt-1 border border-slate-200 rounded-lg divide-y divide-slate-100 bg-white" data-testid="dish-ingredients-table">
            {form.ingredient_quantities.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-slate-400">Nessun ingrediente · cerca o aggiungi qui sotto</div>
            ) : form.ingredient_quantities.map((q, i) => {
              const ing = ingredientDB.find((d) => d.name.toLowerCase() === (q.name || "").toLowerCase());
              const unitPrice = ing ? parseFloat(ing.price_eur) || 0 : 0;
              const w = parseFloat(q.weight_g) || 0;
              const rowCost = (w / 1000) * unitPrice;
              return (
                <div key={i} className="flex items-center gap-2 px-3 py-2" data-testid={`ing-row-${i}`}>
                  <span className="flex-1 text-sm capitalize text-[#0F2741]">{q.name}</span>
                  <span className="text-[10px] text-slate-400 hidden sm:inline">€{unitPrice.toFixed(2)}/{ing?.unit || "kg"}</span>
                  <input
                    type="number" min="0" step="1"
                    value={q.weight_g}
                    onChange={(e) => updateQuantity(i, e.target.value)}
                    className="w-20 px-2 py-1 rounded border border-slate-200 text-sm text-right"
                    data-testid={`ing-weight-${i}`}
                  />
                  <span className="text-xs text-slate-400">g</span>
                  <span className="w-16 text-right text-xs font-semibold text-amber-700">€{rowCost.toFixed(2)}</span>
                  <button type="button" onClick={() => removeIngredient(i)} className="text-slate-400 hover:text-rose-500"><X className="w-4 h-4" /></button>
                </div>
              );
            })}
          </div>
          <div className="relative mt-2">
            <input
              data-testid="input-ingredient-search"
              value={ingQuery}
              onChange={(e) => { setIngQuery(e.target.value); setShowSugg(true); }}
              onFocus={() => setShowSugg(true)}
              onKeyDown={onIngKey}
              placeholder="Cerca o aggiungi ingrediente (es. pomodoro)..."
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-[#2563EB] outline-none"
            />
            {showSugg && suggestions.length > 0 && (
              <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {suggestions.map((i) => (
                  <button key={i.id} type="button" onClick={() => addIngredient(i.name)} className="w-full px-3 py-2 text-left text-sm hover:bg-[#EFF6FF] flex justify-between items-center capitalize">
                    <span>{i.name} <span className="text-xs text-slate-400 normal-case">({i.category})</span></span>
                    <span className="text-xs text-[#1D4ED8] font-semibold">€{i.price_eur}/{i.unit}</span>
                  </button>
                ))}
              </div>
            )}
            {showSugg && ingQuery && suggestions.length === 0 && (
              <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg p-2">
                <button type="button" onClick={() => addIngredient(ingQuery.trim().toLowerCase())} className="text-sm text-[#2563EB] font-semibold w-full text-left">
                  + Aggiungi "<span className="capitalize">{ingQuery.trim()}</span>" come ingrediente custom (verrà aggiunto al DB con prezzo 0)
                </button>
              </div>
            )}
          </div>
          <p className="text-xs text-slate-400 mt-1.5">Imposta la quantità in grammi di ogni ingrediente per ottenere il food cost esatto</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-3 border-t border-slate-100">
          <div className="bg-amber-50 rounded-lg p-3" data-testid="live-cost-preview">
            <p className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">Food cost calcolato</p>
            <p className="font-display text-2xl text-amber-700 mt-1">€ {livePreviewCost.toFixed(2)}</p>
            <p className="text-[10px] text-amber-600 mt-0.5">Calcolato dai pesi sopra · ricalcolato live</p>
          </div>
          <div className="bg-emerald-50 rounded-lg p-3" data-testid="live-sale-preview">
            <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Prezzo di vendita suggerito</p>
            <p className="font-display text-2xl text-emerald-700 mt-1">€ {livePreviewSale.toFixed(2)}</p>
            <p className="text-[10px] text-emerald-600 mt-0.5">Food cost al 30% del prezzo (arrotondato)</p>
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-emerald-700 font-semibold">Override prezzo di vendita (€)</label>
          <input data-testid="input-dish-sale" type="number" step="0.01" value={form.sale_price_eur} onChange={(e) => setForm({ ...form, sale_price_eur: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-emerald-300 bg-emerald-50 font-semibold" />
          <p className="text-[10px] text-slate-400 mt-1">Lascia 0 per usare il prezzo suggerito (food cost = 30% del prezzo, arrotondato)</p>
        </div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-100 bg-white rounded-b-xl flex-shrink-0">
          <button type="button" onClick={onClose} className="btn-ghost">Annulla</button>
          <button type="submit" data-testid="btn-save-dish" className="btn-primary">Salva</button>
        </div>
      </form>
    </div>
  );
}

function defaultWeightForCategory(cat) {
  const map = {
    pasta: 100, riso: 80, impasti: 200, farine: 100, pane: 60,
    carne: 120, pesce: 150,
    latticini: 50,
    verdura: 50, frutta: 80, dolci: 80,
    dispensa: 5, vini: 30,
  };
  return map[cat] || 30;
}
